import { type User as firebase_User } from 'firebase/auth';
import { collection, doc, type DocumentReference } from 'firebase/firestore';
import { getDownloadURL, uploadBytes } from 'firebase/storage';

import {
    getAppStorageFileRef,
    getAuth,
    getFirebase,
    getFirestore,
} from 'services/Firebase';
import { getAppBroker } from 'stores/AppBroker';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { SiteRole } from 'types/Roles';
import { type StarredDocument } from 'types/StarredDocument';
import { UserEvents } from 'types/UserEvents';
import { UserStatus } from 'types/UserStatus';
import { nullOrServerTimestamp, serverTimestamp } from 'utils/FirestoreUtils';

import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';
import StarredDocuments from './StarredDocuments';

export class UserSchema extends DocumentSchema {
    activated = true;
    dateCreated = nullOrServerTimestamp();
    dateModified = nullOrServerTimestamp();
    department = '';
    displayName = '';
    email = '';
    firstName = '';
    lastName = '';
    phoneNumber = '';
    cachedPhotoURL = '';
    photoURL = '';
    role = SiteRole.DEFAULT;

    // Formerly used as an artifact of role evaluation, now retired.
    documentRole: undefined = undefined;

    uid = '';

    starredDocuments: StarredDocument[] = [];

    // These all seem vestigial - types reconstructed from
    // `functions/src/schema/user.ts#UserConverter`
    starredClients: Record<string, string> = {};
    starredCreatives: Record<string, string> = {};
    starredProjects: Record<string, string> = {};

    constructor(authData?: firebase_User | UserSchema | null) {
        super();
        if (!authData) return;
        const names = authData.displayName?.split(' ') || ['', ''];
        this.firstName = names[0];
        this.lastName = names[1];
        this.displayName = authData.displayName || '';
        this.email = authData.email || '';
        this.phoneNumber = authData.phoneNumber || '';
        this.photoURL = authData.photoURL || '';
        this.department = '';
        this.role = SiteRole.DEFAULT;
        this.uid = authData.uid;
        this.dateCreated = serverTimestamp();
        this.dateModified = serverTimestamp();
    }
}

export class UserSchemaPublic {
    department = "";
    displayName = "";
    email = "";
    firstName = "";
    lastName = "";
    cachedPhotoURL = "";
    photoURL = "";
    uid = "";

    constructor(authData?: firebase_User | UserSchemaPublic | null) {
        if (!authData) return;
        const names = authData.displayName?.split(' ') || ['', ''];
        this.firstName = names[0];
        this.lastName = names[1];
        this.displayName = authData.displayName || '';
        this.email = authData.email || '';
        this.photoURL = authData.photoURL || '';
        this.department = '';
        this.uid = authData.uid;
    }
}

export default class User extends DocumentStore<UserSchema, UserSchemaPublic> {
    schema = new UserSchema();
    publicSchema = new UserSchemaPublic();
    collection = collection(getFirestore(), 'users');
    type = DocumentType.USER;
    collectionType = DocumentCollectionType.USER;

    placeholderName = '';
    get role(){ return this.data.role };

    authStatus: UserStatus = UserStatus.DISCONNECTED;
    starred = new StarredDocuments(this);

    get id() {
        return this.data.uid;
    }

    get authenticated() {
        return this.authStatus === UserStatus.LOGGED_IN;
    }

    constructor(parent: unknown, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable();
    }

    onReset = () => {
        this.type = DocumentType.USER;
        this.collectionType = DocumentCollectionType.USER;
        this.placeholderName = '';
        this.authStatus = UserStatus.DISCONNECTED;
        this.starred = new StarredDocuments(this);
    }

    connect() {
        return new Promise(async (resolve) => {
            const onAuthStateChanged = async (
                authUser: firebase_User | null
            ) => {
                this.status = DocumentStatus.LOADING;
                if (authUser) {
                    await this.loadUserData(authUser.uid);
                    if (!this.exists) {
                        await this.createUser(authUser);
                    } else {
                        await this.checkAndUpdatePhoto(authUser);
                    }
                    this.status = DocumentStatus.LOADED;
                    this.authStatus = UserStatus.LOGGED_IN;
                } else {
                    this.reset();
                    this.status = DocumentStatus.LOADED;
                    this.authStatus = UserStatus.LOGGED_OUT;
                }
                console.log('User: Auth status:', this.authStatus);
                resolve(this.authStatus);
            };

            this.disposer.add(getAuth().onAuthStateChanged(onAuthStateChanged));
        });
    }

    login = async () => {
        if (this.authenticated) return;
        try {
            const userCredential = await getFirebase().authSignInWithPopup();
            const authUser = userCredential.user;

            if (!authUser) {
                console.log('User: Error logging in.');
                return false;
            }

            await this.loadUserData(authUser.uid);

            if (!this.exists) {
                // TODO: Create user if it doesn't exist.
            }

            this.authStatus = UserStatus.LOGGED_IN;
            this.dispatch(UserEvents.LOGIN_SUCCESS);
        } catch (e) {
            console.log(e);
            this.dispatch(UserEvents.ERROR);
            this.authStatus = UserStatus.ERROR;
            return;
        }
        await getAppBroker().navigateAfterLogin();
    };

    async loadUserData(uid: string) {
        // Which user document to load is only known once login provides the
        // uid, so it's connected here rather than at construction.
        this.connectDocRef(doc(this.collection, uid));
        const data = await this.load();

        // Check if the user has a Firebase photo URL, if not, upload the photo
        if (
            this.authenticated &&
            !this.data.cachedPhotoURL &&
            this.data.photoURL
        ) {
            const photoURL = await this.cachePhoto(
                this.data.photoURL,
                this.data.uid
            );
            this.data.cachedPhotoURL = photoURL;
            await this.save();
        }

        return data;
    }

    createUser = async (authUser: firebase_User | null) => {
        if (!authUser) return;
        this.data = new UserSchema(authUser);

        await this.checkAndUpdatePhoto(authUser);
        await this.save();
    };

    cachePhoto = async (googlePhotoURL: string, userId: string): Promise<string> => {
        try {
            // Fetch the image from Google's servers
            const response = await fetch(googlePhotoURL);
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.statusText}`);
            }
            const blob = await response.blob();

            // Upload to Firebase Storage
            const file = getAppStorageFileRef(`profile_photos/${userId}`);
            await uploadBytes(file, blob);

            // Get the download URL
            let downloadURL = await getDownloadURL(file);
            if (!downloadURL) {
                throw new Error('Failed to get download URL');
            }
            // Strip the token from the URL
            downloadURL = downloadURL.split('&token=')[0];
            return downloadURL;
        } catch (error) {
            console.error('Error uploading profile photo:', error);
            throw new Error('Failed to upload profile photo');
        }
    }

    checkAndUpdatePhoto = async (authUser: firebase_User | null) => {
        if (!authUser) return;
        if (!this.data.cachedPhotoURL || this.data.photoURL !== authUser.photoURL) {
            if (authUser.photoURL) {
                try {
                    const cachedPhotoURL = await this.cachePhoto(authUser.photoURL, authUser.uid);
                    this.data.cachedPhotoURL = cachedPhotoURL;
                    this.data.photoURL = authUser.photoURL;
                    await this.save();
                } catch (error) {
                    console.error('Failed to update profile photo:', error);
                    // If caching fails, we'll use the original photoURL
                    this.data.photoURL = authUser.photoURL;
                    await this.save();
                }
            }
        }
    }

    onReady = async () => {
        // this.starred.setState(this.data.starredDocuments);
        // this.starred.load();
    }

    onUpdated = (_data: UserSchema) => {
        this.starred.update(this.data.starredDocuments);
    };

    setPlaceholderName = (name: string) => {
        this.placeholderName = name;
    }

    setRole = (role: SiteRole) => {
        this.data.role = role;
        this.save();
    };

    toggleActivated = () => {
        this.data.activated = !this.data.activated;
        this.save();
    }

    logout = () => {
        getAuth().signOut();
        // Not clear if this is necessary or if an event listener also does
        // this; doing it just in case
        this.authStatus = UserStatus.LOGGED_OUT;

        this.reset();
        getAppBroker().afterLogout();
        this.dispatch(UserEvents.LOGGED_OUT);
    };
}
